import { Info } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@prescreen/lib/utils"
import {
  OUTPUT_LOG_HTTP_METHODS,
  type OutputLogBodyField,
  type OutputLogHttpMethod,
} from "@prescreen/lib/integrations/output-log"

export type OutputLogFormState = {
  enabled: boolean
  url: string
  method: OutputLogHttpMethod
  bodyFields: OutputLogBodyField[]
}

export function emptyOutputLogForm(): OutputLogFormState {
  return {
    enabled: false,
    url: "",
    method: "POST",
    bodyFields: [],
  }
}

interface OutputLogConfigurationProps {
  value: OutputLogFormState
  isEditMode: boolean
  onChange: (value: OutputLogFormState) => void
}

export default function OutputLogConfiguration({
  value,
  isEditMode,
  onChange,
}: OutputLogConfigurationProps) {
  const updateField = (index: number, patch: Partial<OutputLogBodyField>) => {
    onChange({
      ...value,
      bodyFields: value.bodyFields.map((field, fieldIndex) =>
        fieldIndex === index ? { ...field, ...patch } : field
      ),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5">
        <h3 className="text-lg font-medium text-gray-900">Output logs</h3>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex shrink-0 rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label="More about: Output logs"
            >
              <Info className="size-4" strokeWidth={1.5} aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            align="start"
            className="max-w-xs text-left text-xs leading-snug"
          >
            Request and response bodies are forwarded unredacted and unmasked, including account
            numbers and other customer data. Authorization headers and the APIM subscription key
            are not included.
          </TooltipContent>
        </Tooltip>
      </div>
      <p className="text-sm text-gray-600">
        Send each partner API call to a third-party service after the response is returned.
        Select a destination to configure it, and select it again to turn logging off.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div
          className={cn(
            "relative rounded-lg border-2 p-4 transition-colors",
            value.enabled
              ? "border-primary bg-primary/5"
              : "border-muted hover:border-muted-foreground/30"
          )}
        >
          <button
            type="button"
            aria-pressed={value.enabled}
            onClick={() => onChange({ ...value, enabled: !value.enabled })}
            className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <div className="font-semibold text-gray-900">Azure Logic App</div>
            <p className="mt-1 text-sm text-gray-600">
              Send the audit record to an Azure Logic App HTTP trigger.
            </p>
          </button>
        </div>
      </div>

      {value.enabled ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="outputLogUrl">Logic App URL</Label>
            <Input
              id="outputLogUrl"
              type="url"
              placeholder="https://example.logic.azure.com/workflows/..."
              value={value.url}
              onChange={(event) => onChange({ ...value, url: event.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="outputLogMethod">HTTP method</Label>
            <select
              id="outputLogMethod"
              value={value.method}
              onChange={(event) =>
                onChange({
                  ...value,
                  method: event.target.value as OutputLogHttpMethod,
                })
              }
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {OUTPUT_LOG_HTTP_METHODS.map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label>Body fields</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  onChange({
                    ...value,
                    bodyFields: [...value.bodyFields, { key: "", value: "", secret: false }],
                  })
                }
              >
                Add field
              </Button>
            </div>
            <p className="text-sm text-gray-600">
              Keys and values are added to the JSON body on every log. Mark a value as secret when
              it is a credential such as an API key or access token.
            </p>
            {isEditMode ? (
              <p className="text-xs text-amber-600">
                Leave a masked secret empty or unchanged to keep the current value.
              </p>
            ) : null}
            {value.bodyFields.length > 0 ? (
              <div className="space-y-2">
                <div className="hidden gap-2 text-xs font-medium text-muted-foreground md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_5.5rem_2.5rem] md:items-center">
                  <span>Key</span>
                  <span>Value</span>
                  <span>Secret</span>
                  <span className="sr-only">Remove</span>
                </div>
                {value.bodyFields.map((field, index) => (
                  <div
                    key={index}
                    className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_5.5rem_2.5rem] md:items-center"
                  >
                    <Input
                      aria-label={`Body field ${index + 1} key`}
                      placeholder="Key"
                      value={field.key}
                      onChange={(event) => updateField(index, { key: event.target.value })}
                    />
                    <Input
                      aria-label={`Body field ${index + 1} value`}
                      placeholder="Value"
                      value={field.value}
                      onChange={(event) => updateField(index, { value: event.target.value })}
                    />
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <Checkbox
                        checked={field.secret}
                        onCheckedChange={(checked) =>
                          updateField(index, { secret: checked === true })
                        }
                        aria-label={`Body field ${index + 1} is secret`}
                      />
                      Secret
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        onChange({
                          ...value,
                          bodyFields: value.bodyFields.filter((_, fieldIndex) => fieldIndex !== index),
                        })
                      }
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}
